import { Box, Dialog, Typography } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { isDesktop } from "react-device-detect";
import { ColorScheme } from "../../../Themes/ColorScheme";
import { ApiContext } from "../../ApiContext";

import { useTranslation } from "react-i18next";
import { ReactComponent as ArrowIcon } from "../../../Assets/Icons/arrowSide.svg";
import { ReactComponent as CalendarIcon } from "../../../Assets/Icons/CalendarIcon_custom_stroke.svg";
import { ReactComponent as DescriptionIcon } from "../../../Assets/Icons/CustomizeIcon.svg";
import BigButton from "../../Common/BigButton";
import { ConfirmAction } from "../../Common/ConfirmAction";
import { DeleteButton } from "../../Common/DeleteButton";
import { TextFieldInput } from "../../Common/TextFieldInput";
import { TextfieldPicker } from "../../Common/TextfieldPicker";

export const ModifyDialog = ({
    users = [],
    handleRefresh,
    dialogOpen,
    closeDialog,
    reservable
}) => {
    const { ApiCall } = useContext(ApiContext);
    const { t } = useTranslation();

    const isSingle = users.length === 1;
    const firstUser = users[0] || {};

    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    const [description, setDescription] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isSingle) {
            setStartTime(new Date(firstUser.from));
            setEndTime(new Date(firstUser.end));
            setDescription(firstUser.description);
        } else if (users.length > 1) {
            // Use first user's data as initial value
            setStartTime(new Date(users[0].from));
            setEndTime(new Date(users[0].end));
            setDescription("");
        }
    }, [users]);

    const handleSave = () => {
        setLoading(true);
        const payload = users.map((user) => ({
            user_id: user.user_id,
            ...(startTime !== null && { from: startTime }),
            ...(endTime !== null && { to: endTime }),
            description
        }));
        ApiCall("PUT", "client/residents/" + reservable.id, payload)
            .then(() => {
                closeDialog(false);
                handleRefresh();
				setLoading(false);
            })
            .catch(() => setLoading(false));
    };

    const handleRemove = () => {
        setLoading(true);
        const userIds = users.map((u) => u.user_id);
        ApiCall("DELETE", "client/residents/" + reservable.id, {
            user_ids: userIds
        })
            .then(() => {
                handleRefresh();
				setLoading(false);
				closeDialog(false);
            })
            .catch((error) => console.log(error))
            .finally(() => setLoading(false));
    };

    return (
        <div style={{ position: "relative" }}>
            <Dialog open={dialogOpen} onClose={() => closeDialog(false)}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        padding: "20px",
                        minWidth: "300px",
                        boxSizing: "border-box",
                        gap: "20px"
                    }}
                >
                    <div style={{flexDirection: "row", display: "flex", width: "100%", justifyContent: "space-between"}}>
                        <div>
                            {isSingle ? (
                                <>
                                    {firstUser.user_name !== "" && (
                                        <Typography
                                            sx={{
                                                fontSize: "20px",
                                                color: ColorScheme.black,
                                                fontWeight: "700"
                                            }}
                                        >
                                            {firstUser.user_name}
                                        </Typography>
                                    )}
                                    {firstUser.email !== "" && (
                                        <Typography sx={{ fontSize: "16px", color: ColorScheme.textSecondary }}>
                                            {firstUser.email}
                                        </Typography>
                                    )}
                                    {firstUser.user_name === "" &&
                                        firstUser.email === "" &&
                                        firstUser.phone !== "" && (
                                            <Typography
                                                sx={{ fontSize: "16px", color: ColorScheme.darkGrey }}
                                            >
                                                {firstUser.phone}
                                            </Typography>
                                        )}
                                </>
                            ) : (
                                <>
									<Typography
										sx={{
											fontSize: "20px",
											color: ColorScheme.black,
											fontWeight: "700"
										}}
									>
										{t("key_management.edit_multiple_users") + " (" + users.length + ")"}
									</Typography>
									<Box sx={{ mt: 1 }}>
										{users.slice(0, 10).map((user, idx) => (
											<Typography
												key={user.user_id || idx}
												sx={{ fontSize: "15px", color: ColorScheme.textSecondary }}
											>
												{user.user_name
													? <>
														{user.user_name}
														{user.email && (
															<span style={{ color: ColorScheme.darkGrey, marginLeft: 4 }}>
																{" (" + user.email + ")"}
															</span>
														)}
													</>
													: user.email || user.phone || t("key_management.unknown_user")}
											</Typography>
										))}
										{users.length > 10 && (
											<Typography sx={{ fontSize: "15px", color: ColorScheme.darkGrey }}>
												...
											</Typography>
										)}
									</Box>
								</>
                            )}
                        </div>
                        <ConfirmAction
                            hideTooltip={true}
                            handleConfirmAction={handleRemove}
                            background={true}
                            buttonText={t("buttons.delete")}
                            useTrashIcon={true}
                        >
                            <div
                                style={{
                                    width: "36px",
                                    height: "36px"
                                }}
                            >
                                <DeleteButton />
                            </div>
                        </ConfirmAction>
                    </div>
                    <TextFieldInput
                        text={description}
                        title={t("textfield_labels.description")}
                        hasProps={
                            <DescriptionIcon
                                stroke={ColorScheme.darkGrey}
                            />
                        }
                        valueChange={(event) => {
                            setDescription(event);
                        }}
                    />

                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "20px",
                            flexDirection: isDesktop ? "row" : "column"
                        }}
                    >
                        <TextfieldPicker
							maskUnlimitedStart={true}
                            value={startTime}
                            noTime={true}
                            label={t("textfield_labels.starts")}
                            fullDate={false}
                            excludePast={false}
                            leanToEndOfDay={false}
                            valueChange={(value) => {
                                if (value === null) {
                                    value = new Date();
                                    value.setHours(0, 0, 0, 0);
                                }
                                const date = new Date(value).toISOString();
                                setStartTime(date);
                            }}
                            hasProps={
                                <CalendarIcon
                                    stroke={ColorScheme.darkGrey}
                                />
                            }
                        />

                        {isDesktop && <div style={{ width: "0px" }} />}
                        {isDesktop && (
                            <div
                                style={{
                                    display: "inline-block",
                                    transform: "rotate(180deg)"
                                }}
                            >
                                <ArrowIcon
                                    stroke={ColorScheme.darkGrey}
                                    fill={ColorScheme.darkGrey}
                                    width={"12px"}
                                    height={"12px"}
                                />
                            </div>
                        )}
                        {isDesktop && <div style={{ width: "0px" }} />}

                        <TextfieldPicker
							maskUnlimitedEnd={true}
                            value={endTime || ""}
                            noEndTime={true}
                            noTime={true}
                            label={t("textfield_labels.ends")}
                            fullDate={false}
                            leanToEndOfDay={true}
                            excludePast={false}
                            valueChange={(value) => {
                                if (value === null) {
                                    value = new Date("2038-01-01T00:00:00Z");
                                }
                                const date = new Date(value).toISOString();
                                setEndTime(date);
                            }}
                            hasProps={
                                <CalendarIcon
                                    stroke={ColorScheme.darkGrey}
                                />
                            }
                        />
                    </div>

                    <Box
                        sx={{
                            flexDirection: "row",
                            display: "flex",
                            width: "100%",
                            justifyContent: "space-between"
                        }}
                    >
                        <BigButton
                            action={() => closeDialog(false)}
                            text={t("buttons.cancel")}
                            textColor={ColorScheme.buttonSecondaryText}
                            backgroundColor={ColorScheme.buttonSecondary}
                            borderColor={ColorScheme.buttonPrimary}
                            width={"47%"}
                        />
                        <BigButton
                            text={t("buttons.save")}
                            backgroundColor={ColorScheme.buttonPrimary}
                            textColor={ColorScheme.buttonPrimaryText}
                            borderColor={ColorScheme.buttonPrimary}
                            action={handleSave}
                            width={"47%"}
                            activity={loading}
                        />
                    </Box>
                </Box>
            </Dialog>
        </div>
    );
};